import { useState } from "react";
import { Check, KeyRound, Loader2, Send, ShieldCheck, X, Users as UsersIcon } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { Field } from "./_shared";

export function InviteUserModal({ onClose, onCreated }) {
  const { apiFetch } = useAuth();
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const genPwd = () => {
    const arr = new Uint8Array(12);
    crypto.getRandomValues(arr);
    const pwd = Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 16);
    setForm((f) => ({ ...f, password: pwd }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await apiFetch("/api/auth/users", { method: "POST", body: JSON.stringify(form) });
      onCreated?.();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div role="dialog"
         className="fixed inset-0 z-40 grid place-items-center p-4 bg-black/80 backdrop-blur-sm"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="invite-modal">
      <div className="w-full max-w-md bg-[var(--srs-surface)] border border-[var(--srs-line)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--srs-line)]">
          <div className="flex items-center gap-3">
            <UsersIcon className="w-4 h-4 text-[#22D3EE]" />
            <div>
              <div className="text-[10px] tracking-[0.25em] font-bold text-[#22D3EE]">INVITE ADMIN</div>
              <div className="text-xs text-[var(--srs-ink-dim)]">Share the email + temp password securely.</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            data-testid="invite-close-btn"
            className="w-8 h-8 grid place-items-center border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] hover:border-[#22D3EE] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Email" testid="invite-email">
            <input type="email" required
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="helper@example.com"
              data-testid="invite-email-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#22D3EE] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] font-mono placeholder:text-[var(--srs-ink-faint)] transition-colors" />
          </Field>
          <Field label="Name (optional)" testid="invite-name">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Helper"
              data-testid="invite-name-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#22D3EE] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors" />
          </Field>
          <Field label="Temporary password (min 8 chars)" testid="invite-password">
            <div className="flex gap-2">
              <input required
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="they'll change this after first login"
                data-testid="invite-password-input"
                className="flex-1 bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#22D3EE] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] font-mono placeholder:text-[var(--srs-ink-faint)] transition-colors" />
              <button type="button" onClick={genPwd}
                data-testid="invite-generate-btn"
                className="px-3 py-3 border border-[var(--srs-line)] hover:border-[#22D3EE] hover:text-[#22D3EE] text-[var(--srs-ink-dim)] text-[10px] tracking-[0.2em] font-bold uppercase transition-colors">
                Generate
              </button>
            </div>
          </Field>
          {error && <div className="text-xs text-[#EF4444]" data-testid="invite-error">✗ {error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              data-testid="invite-cancel-btn"
              className="px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              data-testid="invite-submit-btn"
              className="inline-flex items-center gap-2 px-5 py-2 text-[10px] tracking-[0.2em] font-bold uppercase bg-[#22D3EE] hover:bg-[#1abacf] disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-black transition-colors">
              {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Inviting…</> : <><ShieldCheck className="w-3 h-3" /> Send invite</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ResetPasswordModal({ user, onClose, onDone }) {
  const { apiFetch } = useAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await apiFetch(`/api/auth/users/${encodeURIComponent(user.id)}/reset-password`, {
        method: "POST",
        body: JSON.stringify({ new_password: password }),
      });
      onDone?.();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div role="dialog"
         className="fixed inset-0 z-40 grid place-items-center p-4 bg-black/80 backdrop-blur-sm"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="reset-modal">
      <div className="w-full max-w-md bg-[var(--srs-surface)] border border-[var(--srs-line)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--srs-line)]">
          <div className="flex items-center gap-3 min-w-0">
            <KeyRound className="w-4 h-4 text-[#22D3EE] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.25em] font-bold text-[#22D3EE]">RESET PASSWORD</div>
              <div className="font-mono text-sm text-[var(--srs-ink)] truncate">{user.email}</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            data-testid="reset-close-btn"
            className="w-8 h-8 grid place-items-center border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] hover:border-[#22D3EE] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="New password (min 8 chars)" testid="reset-pwd">
            <input required type="text"
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-pwd-input"
              autoFocus
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#22D3EE] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] font-mono transition-colors" />
          </Field>
          {error && <div className="text-xs text-[#EF4444]" data-testid="reset-error">✗ {error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              data-testid="reset-cancel-btn"
              className="px-4 py-2 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              data-testid="reset-submit-btn"
              className="inline-flex items-center gap-2 px-5 py-2 text-[10px] tracking-[0.2em] font-bold uppercase bg-[#22D3EE] hover:bg-[#1abacf] disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-black transition-colors">
              {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Resetting…</> : <><Check className="w-3 h-3" /> Reset password</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared atoms
// ---------------------------------------------------------------------------
